function eventText(event) {
    const payload = event?.payload || {};
    const value = payload.text ?? payload.message ?? payload.output ?? event?.text;
    return value == null ? "" : String(value);
}

function resultText(row) {
    const value = row?.output ?? row?.text ?? row?.message;
    return value == null ? "" : String(value);
}

function resultInput(row) {
    const value = row?.input ?? row?.message;
    return value == null ? "" : String(value);
}

function upsertTurn(turns, order, turnId) {
    const id = turnId == null ? `timeline-${order.length + 1}` : String(turnId);
    if (!turns.has(id)) {
        turns.set(id, { turnId: id });
        order.push(id);
    }
    return turns.get(id);
}

const LIVE_PENDING_PHASES = new Set(["enviando", "aguardando_resposta", "avaliando"]);

export function buildLiveBubbles({ timeline = [], results = [], progress = null } = {}) {
    const safeTimeline = Array.isArray(timeline) ? timeline : [];
    const safeResults = Array.isArray(results) ? results : [];
    const resultByTurnId = new Map();
    for (const row of safeResults) {
        if (row?.turnId == null) continue;
        resultByTurnId.set(String(row.turnId), row);
    }

    const turns = new Map();
    const order = [];
    for (const event of safeTimeline) {
        if (!event?.kind) continue;
        if (!["user_message", "bot_pending", "bot_reply"].includes(event.kind)) continue;

        const turn = upsertTurn(turns, order, event.turnId);
        if (event.kind === "user_message") {
            turn.userText = eventText(event);
            turn.userAt = event.at;
            turn.caseId = event.caseId ?? event.payload?.caseId ?? turn.caseId;
        }
        if (event.kind === "bot_pending") {
            turn.hasPending = true;
            turn.pendingAt = event.at;
        }
        if (event.kind === "bot_reply") {
            turn.botText = eventText(event);
            turn.botAt = event.at;
            turn.caseId = event.caseId ?? event.payload?.caseId ?? turn.caseId;
        }
    }

    for (const row of safeResults) {
        if (row?.turnId == null || turns.has(String(row.turnId))) continue;
        const turn = upsertTurn(turns, order, row.turnId);
        turn.userText = resultInput(row);
        turn.botText = resultText(row);
        turn.caseId = row.caseId;
    }

    const phase = progress?.phase ? String(progress.phase) : "";
    const turnIndex = Number(progress?.turnIndex) || 0;
    const progressCaseId = progress?.caseId || null;
    const completedForCase = progressCaseId
        ? safeResults.filter((r) => r.caseId === progressCaseId).length
        : safeResults.length;
    const showProgressPending =
        Boolean(progress) &&
        LIVE_PENDING_PHASES.has(phase) &&
        turnIndex > completedForCase;
    const currentTurnId =
        showProgressPending && turnIndex > 0 && turnIndex <= order.length
            ? order[turnIndex - 1]
            : null;

    const bubbles = [];
    for (const turnId of order) {
        const turn = turns.get(turnId);
        const result = resultByTurnId.get(turnId);
        const userText = turn.userText || resultInput(result);
        const resultOutput = resultText(result);
        const botText = turn.botText || resultOutput;
        const caseId = turn.caseId ?? result?.caseId;

        if (userText) {
            bubbles.push({
                role: "user",
                text: userText,
                turnId,
                caseId,
                at: turn.userAt,
            });
        }

        if (botText) {
            bubbles.push({
                role: "bot",
                text: botText,
                pending: false,
                turnId,
                caseId,
                at: turn.botAt ?? result?.at ?? result?.created_at,
                pass: result?.pass,
            });
        } else if (
            turn.hasPending ||
            (turnId === currentTurnId && userText)
        ) {
            bubbles.push({
                role: "pending",
                text: "Gerando resposta...",
                pending: true,
                turnId,
                caseId,
                at: turn.pendingAt,
            });
        }
    }

    // Turno novo ainda sem eventos na timeline (gap entre polls): mostra pending.
    if (
        showProgressPending &&
        !currentTurnId &&
        !bubbles.some((b) => b.role === "pending")
    ) {
        bubbles.push({
            role: "pending",
            text: "Gerando resposta...",
            pending: true,
            turnId: progress?.turnId || `progress-${turnIndex}`,
            caseId: progressCaseId,
            at: null,
        });
    }

    return bubbles;
}

/**
 * Sintetiza timeline a partir de results[] (runs antigas sem timeline persistida).
 * Permite sempre usar FlowTimedReplay e descontinuar o stepper ◀ ▶ do FlowChatReplay.
 */
export function buildTimelineFromResults(results = []) {
    const rows = Array.isArray(results) ? results : [];
    const events = [];
    let cursorMs = Date.parse("2020-01-01T00:00:00.000Z");
    for (const row of rows) {
        if (!row || row.turnId == null) continue;
        const input = row.input;
        const userText = Array.isArray(input)
            ? input.map((part) => String(part ?? "")).join("\n")
            : input == null
              ? ""
              : String(input);
        events.push({
            at: new Date(cursorMs).toISOString(),
            kind: "user_message",
            turnId: row.turnId,
            caseId: row.caseId,
            payload: { text: userText },
        });
        const latency = Number(row.latencyMs);
        const gapMs = Number.isFinite(latency) && latency > 0 ? latency : 1500;
        cursorMs += gapMs;
        events.push({
            at: new Date(cursorMs).toISOString(),
            kind: "bot_reply",
            turnId: row.turnId,
            caseId: row.caseId,
            payload: {
                text: row.output == null ? "" : String(row.output),
                ...(Number.isFinite(latency) ? { latencyMs: latency } : {}),
            },
        });
        cursorMs += 500;
    }
    return events;
}
